// The screens' floating parts handed to a host that draws them (contract.ts
// `floats`): a conversation's way to its latest message, drawn by a native
// app with its own parts instead of the web's glass button.
//
// Promise:
// - Only the kinds the host says it draws (`ShellHost.floats`) go to it; for
//   those the web draws nothing (`useShellFloat` returns true). Every other
//   host, and a plain browser, keeps the web's own parts.
// - The host gets every part shown now in one `floats` message, sent again
//   only when that list changes. A part goes from the list when its screen
//   hides it or goes.
// - Pressed in the host (`action` with its id), it runs the handler the screen
//   gave last.
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { MeAppContext } from "../../app/context";
import {
  SHELL_CONTRACT_VERSION,
  type ShellFloat,
  type ShellFloatKind,
} from "./contract";
import type { ShellHost } from "./outside";

/** A floating part as a screen hands it: the data and what pressing does. */
export interface ShellFloatInput extends ShellFloat {
  onPress(): void;
}

export interface FloatStore {
  set(owner: object, input: { current: ShellFloatInput | null } | null): void;
  /** Runs the part with this id; false when no part has it. */
  press(id: string): boolean;
}

export const FloatContext = createContext<FloatStore | null>(null);

export function createFloatStore(host: ShellHost): FloatStore {
  // Mounted order: for the same id the last one wins.
  let entries: {
    owner: object;
    input: { current: ShellFloatInput | null };
  }[] = [];
  let sent = "[]";
  const shown = () => {
    const byId = new Map<string, ShellFloatInput>();
    for (const { input } of entries)
      if (input.current) byId.set(input.current.id, input.current);
    return [...byId.values()];
  };
  const send = () => {
    const floats: ShellFloat[] = shown().map(({ onPress: _, ...f }) => f);
    const json = JSON.stringify(floats);
    if (json === sent) return;
    sent = json;
    host.post({ version: SHELL_CONTRACT_VERSION, type: "floats", floats });
  };
  return {
    set(owner, input) {
      const at = entries.findIndex((e) => e.owner === owner);
      if (input && at >= 0) entries[at] = { owner, input };
      else if (input) entries.push({ owner, input });
      else if (at >= 0) entries = entries.filter((e) => e.owner !== owner);
      else return;
      send();
    },
    press(id) {
      const part = shown().find((f) => f.id === id);
      part?.onPress();
      return part !== undefined;
    },
  };
}

/**
 * Inside what the web lays over the whole screen while the host's parts step
 * aside (a phone's overlay: a chat's side): its post box and floating parts
 * are the web's own, as in a browser.
 */
export const HostAsideContext = createContext(false);

/** Whether the host draws this kind of floating part (then the web draws none of it). */
export function useHostDrawsFloat(kind: ShellFloatKind): boolean {
  // Also outside the Me app (a box elsewhere): then no host.
  const host = useContext(MeAppContext)?.extensions.shellHost;
  const aside = useContext(HostAsideContext);
  return !aside && (host?.floats?.has(kind) ?? false);
}

/**
 * Hands a floating part to the host while it is shown (null: not shown), when
 * the host draws its kind. Returns whether it does: then the web draws none.
 */
export function useShellFloat(
  kind: ShellFloatKind,
  input: ShellFloatInput | null,
): boolean {
  const draws = useHostDrawsFloat(kind);
  const store = useContext(FloatContext);
  const [owner] = useState(() => ({}));
  const latest = useRef(input);
  latest.current = input;
  const key = input ? JSON.stringify({ ...input, onPress: undefined }) : null;
  // Tells the host (an outside system) when the part's data changes; the
  // handler is read from `latest` when pressed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `key` stands for the data
  useEffect(() => {
    if (!store || !draws) return;
    store.set(owner, latest.current ? latest : null);
  }, [store, draws, owner, key]);
  useEffect(
    () => () => {
      store?.set(owner, null);
    },
    [store, owner],
  );
  return draws && store !== null;
}
